import { PaginateEntity } from '@/shared/entities/PaginateEntity'
import { CouponsRepository } from '../../domain/repositories/CouponsRepository'
import type { CouponFilterEntity } from '../../domain/entities/CouponFilterEntity'
import { CouponFilterModel } from '../models/CouponFilterModel'
import { CouponMiniModel } from '../models/CouponMiniModel'
import type { CreateCouponEntity } from '../../domain/entities/CreateCouponEntity'
import { CreateCouponModel } from '../models/CreateCouponModel'
import type { CouponEntity } from '../../domain/entities/CouponEntity'
import { CouponModel } from '../models/CouponModel'
import type { UserMiniEntity } from '@/features/user/domain/entities/UserMiniEntity'
import { UserMiniModel } from '@/features/user/data/models/UserMiniModel'
import type { CreateUpdateBranchEntity } from '@/features/branch/domain/entities/CreateUpdateBranchEntity'
import type { BranchMiniEntity } from '@/features/branch/domain/entities/BranchMiniEntity'
import { BranchMiniModel } from '@/features/branch/data/models/BranchMiniModel'
import { ChannelMiniModel } from '@/features/channel/data/models/ChannelMiniModel'
import type { ChannelMiniEntity } from '@/features/channel/domain/entities/ChannelMiniEntity'
import type { CouponMiniEntity } from '../../domain/entities/CouponMiniEntity'
import {
  CouponsService,
  type CouponRevelationListedAdminDto,
  type PostCouponAdminRequest,
  type PutCouponAdminRequest
} from '@/backend/market'
import type { UserGroupEntity } from '@/features/userGroup/domain/entities/UserGroupEntity'
import { UserGroupModel } from '@/features/userGroup/data/models/UserGroupModel'
import { CouponRevelationFilterModel } from '../models/CouponRevelationFilterModel'
import type { CouponRevelationFilterEntity } from '../../domain/entities/CouponRevelationFilterEntity'
import { RevelationMiniModel } from '../models/RevelationMiniModel'
import { CouponRegionModel } from '../models/CouponRegionModel'
import type { CouponRegionEntity } from '../../domain/entities/CouponRegionEntity'
import { CreateUpdateCouponRegionModel } from '../models/CreateUpdateCouponRegionModel'

export class CouponsRepositoryImpl implements CouponsRepository {
  async getCoupons(
    filterData: CouponFilterEntity
  ): Promise<PaginateEntity<CouponMiniModel[]> | { error: true }> {
    const couponFilterModel = new CouponFilterModel(filterData)
    try {
      const response = await CouponsService.getCouponsAdmin(couponFilterModel)
      const couponMiniModels = response.items?.map(
        (item) => new CouponMiniModel(item as CouponMiniEntity)
      )
      const responseModel = new PaginateEntity<CouponMiniModel[]>(response, couponMiniModels)

      return responseModel
    } catch {
      return { error: true }
    }
  }

  async createCoupon(data: CreateCouponEntity): Promise<{ success: true } | { error: true }> {
    const createCouponModel = new CreateCouponModel(data)
    try {
      await CouponsService.createCouponAdmin({
        requestBody: createCouponModel as PostCouponAdminRequest
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async getCouponById(id: number): Promise<CouponEntity | { error: true }> {
    try {
      const response = await CouponsService.getCouponAdmin({ id })
      const couponModel = new CouponModel(response as CouponEntity)
      return couponModel
    } catch {
      return { error: true }
    }
  }

  async updateCoupon(
    id: number,
    data: CreateCouponEntity
  ): Promise<{ success: true } | { error: true }> {
    const createCouponModel = new CreateCouponModel(data)
    try {
      await CouponsService.updateCouponAdmin({
        id,
        requestBody: createCouponModel as PutCouponAdminRequest
      })

      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async getCouponUsersGroups(couponId: number): Promise<UserGroupEntity[] | { error: true }> {
    try {
      const response = await CouponsService.getGroupsAdmin({ id: couponId })
      const userMiniModels = response.groups?.map((item) => new UserGroupModel(item))
      return userMiniModels ?? []
    } catch {
      return { error: true }
    }
  }

  async updateCouponUsersGroups(
    couponId: number,
    couponData: CreateCouponEntity
  ): Promise<{ success: true } | { error: true }> {
    const createUpdateCouponModel = new CreateCouponModel(couponData as CreateCouponEntity)
    try {
      await CouponsService.putGroupsAdmin({
        id: couponId,
        requestBody: {
          groupIds: createUpdateCouponModel.groupIds
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async getCouponUsers(couponId: number): Promise<UserMiniEntity[] | { error: true }> {
    try {
      const response = await CouponsService.getUsersAdmin({ id: couponId })
      const userMiniModels = response.users?.map(
        (item) => new UserMiniModel(item as UserMiniEntity)
      )
      return userMiniModels ?? []
    } catch {
      return { error: true }
    }
  }

  async updateCouponUser(
    couponId: number,
    couponData: CreateUpdateBranchEntity
  ): Promise<{ success: true } | { error: true }> {
    const createUpdateCouponModel = new CreateCouponModel(couponData)
    try {
      await CouponsService.updateUsersAdmin({
        id: couponId,
        requestBody: {
          userIds: createUpdateCouponModel.userIds
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async getCouponBranches(couponId: number): Promise<BranchMiniEntity[] | { error: true }> {
    try {
      const response = await CouponsService.getBranchesAdmin({ id: couponId })
      const branchMiniModels = response.branches?.map((item) => new BranchMiniModel(item))
      return branchMiniModels ?? []
    } catch {
      return { error: true }
    }
  }

  async updateCouponBranch(
    couponId: number,
    couponData: CreateUpdateBranchEntity
  ): Promise<{ success: true } | { error: true }> {
    const createUpdateCouponModel = new CreateCouponModel(couponData)
    try {
      await CouponsService.updateBranchesAdmin({
        id: couponId,
        requestBody: {
          branchIds: createUpdateCouponModel.branchIds as number[]
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async getCouponRegions(couponId: number): Promise<CouponRegionEntity[] | { error: true }> {
    try {
      const response = await CouponsService.getRegionsAdmin({ id: couponId })
      const regionMiniModels = response.regions?.map((item) => new CouponRegionModel(item))
      return regionMiniModels ?? []
    } catch {
      return { error: true }
    }
  }

  async updateCouponRegion(
    couponId: number,
    regionData: CouponRegionEntity[]
  ): Promise<{ success: true } | { error: true }> {
    const createUpdateCouponRegionModels = regionData.map(
      (region) => new CreateUpdateCouponRegionModel(region)
    )
    try {
      await CouponsService.updateRegionsAdmin({
        id: couponId,
        requestBody: {
          regions: createUpdateCouponRegionModels
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async getCouponChannels(couponId: number): Promise<ChannelMiniEntity[] | { error: true }> {
    try {
      const response = await CouponsService.getChannelsAdmin({ id: couponId })
      const channelMiniModels = response.channels?.map((item) => new ChannelMiniModel(item))
      return channelMiniModels ?? []
    } catch {
      return { error: true }
    }
  }

  async updateCouponChannel(
    couponId: number,
    couponData: CreateCouponEntity
  ): Promise<{ success: true } | { error: true }> {
    const createUpdateCouponModel = new CreateCouponModel(couponData)
    try {
      await CouponsService.updateChannelsAdmin({
        id: couponId,
        requestBody: {
          channelIds: createUpdateCouponModel.channelIds
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async getCouponRevelation(
    filterData: CouponRevelationFilterEntity
  ): Promise<PaginateEntity<RevelationMiniModel[]> | { error: true }> {
    const couponRevelationFilterModel = new CouponRevelationFilterModel(filterData)
    try {
      const response = await CouponsService.getCouponRevelationsAdmin(
        couponRevelationFilterModel as CouponRevelationFilterEntity
      )
      const revelationMiniModels = response.items?.map(
        (item) => new RevelationMiniModel(item as CouponRevelationListedAdminDto)
      )
      const responseModel = new PaginateEntity<RevelationMiniModel[]>(
        response,
        revelationMiniModels
      )

      return responseModel
    } catch {
      return { error: true }
    }
  }

  async deleteCoupon(id: number): Promise<{ success: true } | { error: true }> {
    try {
      await CouponsService.deleteCouponAdmin({
        id
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
}
